import { useUserStore } from '@/src/store/useUserStore';
import { ReactNode, useEffect, useState } from 'react';
import { cn } from '@/src/lib/utils';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { ambiance } = useUserStore();
  const [activeTheme, setActiveTheme] = useState(ambiance.theme);
  const [nightModeActive, setNightModeActive] = useState(false);

  useEffect(() => {
    if (ambiance.theme === 'circadian') {
      const hour = new Date().getHours();
      if (hour >= 6 && hour < 12) setActiveTheme('amber'); // Morning
      else if (hour >= 12 && hour < 17) setActiveTheme('charcoal'); // Afternoon
      else if (hour >= 17 && hour < 21) setActiveTheme('ocean'); // Evening
      else setActiveTheme('grayscale'); // Night
    } else {
      setActiveTheme(ambiance.theme);
    }
  }, [ambiance.theme]);

  useEffect(() => {
    if (ambiance.autoNightMode) {
      const hour = new Date().getHours();
      // Activate night mode between 8 PM and 6 AM
      setNightModeActive(hour >= 20 || hour < 6);
    } else {
      setNightModeActive(false);
    }
  }, [ambiance.autoNightMode]);

  const themeClasses = {
    charcoal: 'bg-zinc-950 text-zinc-100 selection:bg-emerald-500/30',
    amber: 'bg-[#1a130d] text-amber-50 selection:bg-amber-500/30',
    forest: 'bg-[#0d1a13] text-emerald-50 selection:bg-emerald-500/30',
    ocean: 'bg-[#0d131a] text-sky-50 selection:bg-sky-500/30',
    grayscale: 'bg-black text-neutral-300 selection:bg-neutral-500/30',
  };

  const gradientClasses = {
    charcoal: 'from-emerald-900/20 via-zinc-950 to-zinc-950',
    amber: 'from-amber-900/20 via-[#1a130d] to-[#1a130d]',
    forest: 'from-emerald-900/20 via-[#0d1a13] to-[#0d1a13]',
    ocean: 'from-sky-900/20 via-[#0d131a] to-[#0d131a]',
    grayscale: 'from-neutral-900/20 via-black to-black',
  };

  const currentThemeClass = themeClasses[activeTheme as keyof typeof themeClasses] || themeClasses.charcoal;
  const currentGradientClass = gradientClasses[activeTheme as keyof typeof gradientClasses] || gradientClasses.charcoal;

  // Calculate color temperature overlay
  // 0 = cool blue, 50 = neutral, 100 = warm amber
  const tempValue = ambiance.colorTemperature ?? 50;
  const isWarm = tempValue > 50;
  const tempOpacity = Math.abs(tempValue - 50) / 100; // 0 to 0.5
  
  // Apply auto night mode (forces warm amber overlay)
  const finalWarmOverlay = nightModeActive ? Math.max(0.3, isWarm ? tempOpacity : 0) : (isWarm ? tempOpacity : 0);
  const finalCoolOverlay = nightModeActive ? 0 : (!isWarm ? tempOpacity : 0);

  // Motion classes
  const getMotionClass = () => {
    if (ambiance.seizureSafeMode) return 'animate-none';
    
    switch (ambiance.motion) {
      case 'static': return 'animate-none';
      case 'reduced': return 'animate-pulse duration-10000';
      case 'light': return 'animate-pulse duration-5000';
      case 'immersive': return 'animate-pulse duration-3000';
      default: return 'animate-pulse duration-5000';
    }
  };

  return (
    <div className={cn(`flex h-screen font-sans relative overflow-hidden ${ambiance.seizureSafeMode ? '' : 'transition-colors duration-1000'}`, currentThemeClass)}>
      {/* Color Temperature Overlay */}
      <div 
        className={`fixed inset-0 pointer-events-none mix-blend-overlay z-50 ${ambiance.seizureSafeMode ? '' : 'transition-opacity duration-1000'}`}
        style={{
          backgroundColor: finalWarmOverlay > 0 ? `rgba(255, 150, 0, ${finalWarmOverlay})` : `rgba(0, 150, 255, ${finalCoolOverlay})`
        }}
      />

      {children}

      {/* Backgrounds */}
      {ambiance.backgroundType === 'gradient' && (
        <div className={cn(
          `fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] -z-10 pointer-events-none ${ambiance.seizureSafeMode ? '' : 'transition-colors duration-1000'}`,
          currentGradientClass,
          ambiance.lightIntensity === 'dim' ? 'opacity-50' : ambiance.lightIntensity === 'bright' ? 'opacity-100' : 'opacity-80',
          getMotionClass()
        )} />
      )}
      
      {ambiance.backgroundType === 'solid' && (
        <div className={cn(
          `fixed inset-0 -z-10 pointer-events-none ${ambiance.seizureSafeMode ? '' : 'transition-colors duration-1000'}`,
          ambiance.lightIntensity === 'dim' ? 'bg-black/50' : ambiance.lightIntensity === 'bright' ? 'bg-white/5' : 'bg-transparent'
        )} />
      )}

      {ambiance.backgroundType === 'custom' && ambiance.customBackgroundImage && (
        <div 
          className={cn(
            "fixed inset-0 -z-10 bg-cover bg-center transition-all duration-1000 pointer-events-none",
            ambiance.lightIntensity === 'dim' ? 'opacity-30' : ambiance.lightIntensity === 'bright' ? 'opacity-80' : 'opacity-50',
            getMotionClass()
          )}
          style={{ 
            backgroundImage: `url(${ambiance.customBackgroundImage})`,
            filter: 'blur(10px) brightness(0.7)'
          }} 
        />
      )}

      {/* Fallback for custom if no image */}
      {ambiance.backgroundType === 'custom' && !ambiance.customBackgroundImage && (
        <div className={cn(
          "fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] -z-10 transition-colors duration-1000 pointer-events-none",
          currentGradientClass,
          'opacity-50'
        )} />
      )}
    </div>
  );
}
